/**
 * Region-based wine maturity windows for drinking window calculations.
 * These are heuristics based on general region characteristics.
 *
 * - early: Age when wine begins to enter its drinking window
 * - peak: Age when wine is typically at its peak
 * - late: Age after which wine may be past its optimal drinking window
 *
 * All values are in years from vintage.
 */
interface MaturityWindow {
  early: number;
  peak: number;
  late: number;
}

const MATURITY_WINDOWS: Record<string, MaturityWindow> = {
  bordeaux: { early: 8, peak: 15, late: 30 },
  burgundy: { early: 6, peak: 12, late: 25 },
  champagne: { early: 5, peak: 10, late: 20 },
  rhone: { early: 5, peak: 12, late: 25 },
  italy: { early: 6, peak: 12, late: 25 },
  usa: { early: 5, peak: 10, late: 20 },
  'rest-of-world': { early: 4, peak: 8, late: 15 },
  other: { early: 4, peak: 8, late: 15 },
};

export type MaturityStatus = 'approaching' | 'peak' | 'past' | null;

/**
 * Determines the maturity status of a wine based on its vintage and region.
 * Uses region-based heuristics to estimate drinking windows.
 *
 * @param vintage - The vintage year as a string
 * @param region - The wine region (cultWinesAllocationRegion)
 * @returns MaturityStatus indicating the wine's drinking window status
 */
export const getMaturityStatus = (vintage: string | undefined, region: string | undefined): MaturityStatus => {
  if (!vintage) return null;

  const vintageYear = parseInt(vintage, 10);
  if (Number.isNaN(vintageYear)) return null;

  const currentYear = new Date().getFullYear();
  const age = currentYear - vintageYear;

  // Normalize region to lowercase for lookup
  const normalizedRegion = region?.toLowerCase()?.replace(/\s+/g, '-') || 'other';
  const window = MATURITY_WINDOWS[normalizedRegion] || MATURITY_WINDOWS.other;

  // Within 2 years of entering peak window
  if (age >= window.early - 2 && age < window.early) {
    return 'approaching';
  }

  // In peak drinking window
  if (age >= window.early && age <= window.peak + 5) {
    return 'peak';
  }

  // Past optimal window (5+ years past peak)
  if (age > window.late) {
    return 'past';
  }

  return null;
};
